import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSecureConfigLink } from '../hooks/useSource';
import { sourceService } from '../services/source.service';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { usePageBranding } from '../hooks/usePageBranding';
import pliableLogo from '../assets/images/pliable/logo_with_text_black.png';

const DEFAULT_TITLE = 'Pliable';

export default function SecureConfigFillPage() {
  const { code } = useParams<{ code: string }>();
  const { data: linkDetails, isLoading, error } = useSecureConfigLink(code);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const logoPath = linkDetails?.branding?.logo_path || pliableLogo;
  const brandName = linkDetails?.branding?.page_title || DEFAULT_TITLE;

  usePageBranding(linkDetails?.branding);

  const handleSubmit = async () => {
    if (!code) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await sourceService.submitSecureConfigLink(code, values);
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.payload?.error || err?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !linkDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">Link Unavailable</h2>
            <p className="text-sm text-muted-foreground">
              This link may have expired, already been used, or is invalid.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <img src={logoPath} alt={brandName} className="h-8 mx-auto" />
            <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
            <h2 className="text-lg font-semibold">Thank You</h2>
            <p className="text-sm text-muted-foreground">
              Your configuration details have been submitted successfully. The requesting team has been notified.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-3">
          <img src={logoPath} alt={brandName} className="h-8 mx-auto" />
          {linkDetails.icon_path && (
            <img
              src={linkDetails.icon_path}
              alt=""
              className="h-10 w-10 mx-auto"
            />
          )}
          <CardTitle className="text-xl">Configure {linkDetails.source_name}</CardTitle>
          <p className="text-sm text-muted-foreground">
            Please provide the requested configuration details below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {linkDetails.fields.map((field) => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>{field.label}</Label>
              {field.description && (
                <p className="text-xs text-muted-foreground">{field.description}</p>
              )}
              {field.field_type === 'LONG_TEXT' ? (
                <Textarea
                  id={field.name}
                  value={values[field.name] || ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  rows={4}
                  placeholder={field.label}
                />
              ) : (
                <Input
                  id={field.name}
                  type={field.is_secure ? 'password' : 'text'}
                  value={values[field.name] || ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={field.label}
                />
              )}
            </div>
          ))}

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground pt-2">
            Powered by {brandName}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
